import * as React from "react";
const { useState, useEffect } = React;
import markdownIt from "markdown-it";
import moment from "moment-timezone";
import {
    Input,
    Button,
    Upload,
    Modal,
    Layout,
    Menu,
    message,
    Radio,
    Dropdown,
    Checkbox,
    Divider,
    Typography,
    Space,
    Progress,
    Alert,
    Switch,
    Tabs,
    Splitter,
    Flex,
    Spin,
} from 'antd';

import {
    UploadOutlined,
    ArrowUpOutlined,
    CodeOutlined,
    HistoryOutlined,
    CopyOutlined,
    DownloadOutlined,
    EyeOutlined,
    ExportOutlined,
    PlusOutlined,
    EditOutlined,
    MessageOutlined,
    UserSwitchOutlined,
    SettingOutlined,
    ReloadOutlined,
    DeleteOutlined,
    FileTextOutlined,
    CloseOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    EyeInvisibleOutlined,
} from '@ant-design/icons';;

const { TextArea } = Input;
const { Sider, Content } = Layout;
const CheckboxGroup = Checkbox.Group;
const { Text } = Typography;
const regex = /[()\/#\s'"\[\]\{\}:=;+&%^@,\-\\]/g;
const userNameRegex = /[()\/#\s'"\[\]\{\}:=.;+&%^@,\-\\]/g;
// Constants
const PLAIN_OPTIONS = [
    { label: "Notify", value: "notify" },
    { label: "Create App", value: "createApp" },
    { label: "Update File", value: "updateFile" },
    { label: "DB Tool", value: "dbTool" },
];

const DEFAULT_CHECKED_LIST = [];

const SIDEBAR_BUTTON_STYLE = {
    width: "100%",
    textAlign: "left",
    padding: "8px 12px",
    color: "#fff",
    height: "auto",
    display: "flex",
    alignItems: "center",
    gap: "8px",
};

// Markdown Configuration
const markdown = markdownIt({
    html: false,
    typographer: true,
    highlight: function (code, lang = "javascript") {
        lang = "javascript";
        const uuid = uuidv4();
        if (lang && hljs.getLanguage(lang)) {
            try {
                return `<pre style="background-color: #2d2d2d;color: #f0f0f0;padding: 10px;border-radius: 5px;" id="codeBlock">${hljs.highlight(code, { language: lang, ignoreIllegals: true }).value
                    } </pre>`;
            } catch (__) { }
        }

        return (
            `<div class="whitespace-pre-line w-full rounded-lg bg-black-900 px-4 pb-4 relative font-mono font-normal text-sm text-slate-200">
        <div class="w-full flex items-center absolute top-0 left-0 text-slate-200 bg-stone-800 px-4 py-2 text-xs font-sans justify-between rounded-t-md">
          <div class="flex gap-2"><code class="text-xs"></code></div>
          <button data-code-snippet data-code="code-${uuid}" class="flex items-center gap-x-2">
            <svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
            <p>Copy code</p>
          </button>
        </div>
      <pre class="whitespace-pre-wrap">` +
            HTMLEncode(code, "javascript") +
            "</pre></div>"
        );
    },
});

// Main Component
const GenAIChatInterface = ({
    collection,
    recordId,
    renderingSource,
    selectedApp,
    entryComponent,
    chatWindowHeight,
    handleAiAssist,
}) => {
    const [collapsed, setCollapsed] = useState(true);
    const apiService = new ApiService();
    // State Management
    const [selectedAiOperation, setSelectedAiOperation] = useState({
        application_rag: true,
    });
    const [chatMetaData, setChatMetaData] = useState({});
    const [currentSessionId, setCurrentSessionId] = useState("");
    const [isHover, setHover] = useState({});
    const [messageApi, contextHolder] = message.useMessage();
    const [layoutClasses, setLayoutClasses] = useState(
        "chat-container fullscreen",
    );
    const [showPreview, setShowPreview] = useState(false);
    const [newChat, setNewChat] = useState(true);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [fileList, setFileList] = useState([]);
    const [disableChat, setDisableChat] = useState(false);
    const [fileProgress, setFileProgress] = useState({});
    const [persona, setPersona] = useState("Developer");
    const [open, setOpen] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [history, setHistory] = useState([]);
    const [checkedList, setCheckedList] = useState(DEFAULT_CHECKED_LIST);
    const [appComponents, setAppComponents] = useState([]);
    const [showThinking, setShowThinking] = useState({});
    const [isThinking, setIsThinking] = useState(false);
    const [thinkingPhrase, setThinkingPhrase] = useState("Thinking");

    // Dynamic Context State
    const [activeCollection, setActiveCollection] = useState(collection);
    const [activeRecordId, setActiveRecordId] = useState(recordId);
    const [activeApp, setActiveApp] = useState(selectedApp);

    // Context Extractor from URL
    useEffect(() => {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const searchParams = new URLSearchParams(window.location.search);

        if (!collection && pathSegments.length >= 2) {
            // Assumes routing pattern structure like /:collection/:recordId etc.
            setActiveCollection(pathSegments[0]);
        }
        if (!recordId && pathSegments.length >= 2) {
            setActiveRecordId(pathSegments[1]);
        }
        if (!selectedApp && searchParams.has('pagename')) {
            setActiveApp(searchParams.get('pagename'));
        }
    }, [collection, recordId, selectedApp]);

    // Cycle thinking phrases when active
    useEffect(() => {
        let interval;
        if (showSpinner || isThinking) {
            const phrases = [
                "Thinking",
                "Analyzing",
                "Deciphering",
                "Unraveling",
                "Unwrapping",
                "Processing",
                "Connecting dots"
            ];
            interval = setInterval(() => {
                setThinkingPhrase(phrases[Math.floor(Math.random() * phrases.length)]);
            }, 1000);
        } else {
            setThinkingPhrase("Thinking");
        }
        return () => clearInterval(interval);
    }, [showSpinner, isThinking]);

    const [pastedFileName, setPastedFileName] = useState(null);
    const [originalPastedText, setOriginalPastedText] = useState("");
    const [isPastePreviewOpen, setIsPastePreviewOpen] = useState(false);
    const [showSaveSpinner, setShowSaveSpinner] = useState(false);

    // Global style for animated ellipsis
    useEffect(() => {
        if (!document.getElementById("obto-animated-ellipsis")) {
            const style = document.createElement("style");
            style.id = "obto-animated-ellipsis";
            style.innerHTML = `
                @keyframes obto-ellipsis {
                    0% { content: ''; }
                    25% { content: '.'; }
                    50% { content: '..'; }
                    75% { content: '...'; }
                    100% { content: ''; }
                }
                .obto-animated-ellipsis::after {
                    content: '';
                    animation: obto-ellipsis 1.5s infinite steps(1, end);
                }
                .obgenai-markdown h1 { font-size: 1.75rem !important; margin-bottom: 0.5rem; }
                .obgenai-markdown h2 { font-size: 1.5rem !important; margin-bottom: 0.5rem; }
                .obgenai-markdown h3 { font-size: 1.25rem !important; margin-bottom: 0.5rem; }
                .obgenai-markdown h4 { font-size: 1.1rem !important; margin-bottom: 0.5rem; }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // Add these handlers for modal interaction
    const showPastePreview = () => setIsPastePreviewOpen(true);
    const hidePastePreview = () => setIsPastePreviewOpen(false);

    const [domain] = useState("staging");
    const [userName] = useState("ai.admin");
    const [selectedFolder, setSelectedFolder] = useState("general");
    // Utility Functions
    const [toolStatus, setToolStatus] = useState("");

    const copyCodeModern = async () => {
        try {
            const codeBlock = document.getElementById("codeBlock");
            // const successMessage = document.getElementById("successMessage");
            await navigator.clipboard.writeText(codeBlock.textContent);
            // console.log("copyCodeModern");
            messageApi.open({
                type: "success",
                content: "Code Copied",
                duration: 10,
            });
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const saveCode = async () => {
        try {
            setShowSaveSpinner(true);
            setTimeout(() => {
                messageApi.open({
                    type: "success",
                    content: "Code Updated",
                    duration: 10,
                });

                setShowSaveSpinner(false);
            }, 2000);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const uid = function () {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };

    const generateUniqueId = (length = 10) => {
        const characters =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let uniqueId = "";

        for (let i = 0; i < length; i++) {
            uniqueId += characters.charAt(
                Math.floor(Math.random() * characters.length),
            );
        }

        return uniqueId;
    };

    const setFileName = (filename) => {
        return `${generateUniqueId()}_${domain}_${selectedFolder}_${userName.replace(
            userNameRegex,
            "",
        )}_${filename.replace(regex, "")}`;
    };

    const handleFile = (fileInfo) => {
        // console.log({ e });
        let fileUrlPrefix = "";
        const file = fileInfo.file;
        const tempFileList = [...fileList, file];
        const fileName = setFileName(file.name);
        const mixedfile = file.type.split("/");
        const filetype = mixedfile[0]; // (image, video)
        if (filetype === "image") {
            fileUrlPrefix = `https://${domain}.obto.co/ms/filereader.bto?n=`;
        }

        if (filetype === "video") {
            fileUrlPrefix = `https://${domain}.obto.co/ms/videoplayer.bto?n=`;
        }

        if (filetype === "application" && typeof mixedfile[1] !== "undefined") {
            fileUrlPrefix = `https://${domain}.obto.co/ms/filereader.bto?n=`;
        }

        const fullFileUrl = `${fileUrlPrefix}${fileName}`;
        const TOKEN = localStorage.getItem("wsToken");
        setFileList([...fileList, file]);
        const upload = new tus.Upload(file, {
            endpoint: "https://fileupload.obto.workers.dev/",
            chunkSize: 100 * 1024 * 1024,
            uploadSize: file.size,
            parallelUploads: 1,
            headers: {
                Filename: fileName,
                source: selectedFolder,
                SourceDomain: "staging.obto.co", //`${domain}.obto.co`,
                "Access-Control-Allow-Headers": "Upload-Offset",
                "Access-Control-Expose-Headers": "Upload-Offset",
                "Access-Control-Allow-Origin": "*",
                Authorization: "Bearer " + TOKEN,
            },
            retryDelays: [0, 3000, 5000, 15000, 20000],
            metadata: {
                filename: fileName,
                filetype: file.type,
                filesize: file.size,
                source: selectedFolder,
                collection: "notification",
                document: "global",
            },
            onError: (error) => {
                console.log("Failed because: " + JSON.stringify(error));
                // observer.error(error);
                // throw error;
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                const percentage = (bytesUploaded / bytesTotal).toFixed(2);
                console.log("Pushing.....", `file:uploading:${fileName}`);

                let tempFileProgress = { ...fileProgress };
                tempFileProgress[file.name] = percentage;
                setFileProgress(tempFileProgress);

                // setFileList((prevFileList) => {
                //   // Create a new array without the current file (if it exists)
                //   const filteredList = prevFileList.filter(
                //     (f) => f.name !== file.name,
                //   );

                //   // Add the updated file
                //   const updatedFile = {
                //     ...file,
                //     name: file.name,
                //     percent: parseFloat(percentage),
                //     status: percentage === "100" ? "done" : "uploading",
                //   };

                //   // Add the new file and ensure we keep only the last 2 files
                //   const newList = [...filteredList, updatedFile];
                //   return newList; //.slice(-2);
                // });
            },
            onSuccess: () => {
                // console.log("Upload finished");
                console.log(`Upload finished: ${fileName}`, fullFileUrl);
                // Decouple this.

                // setFileList((prevFileList) => {
                //   // Remove the current file from the list (if it exists)
                //   const filteredList = prevFileList.filter(
                //     (f) => f.name !== file.name,
                //   );

                //   // Create the completed file object
                //   const completedFile = {
                //     ...file,
                //     name: file.name,
                //     status: "done",
                //     percent: 100,
                //     url: fullFileUrl,
                //   };

                //   // Add the completed file and ensure we keep only the last 2 files
                //   const newList = [...filteredList, completedFile];
                //   return newList; //.slice(-2);
                // });

                // const saveImage = async () => {
                //   const newimage = {
                //     active: true,
                //     name: fileName,
                //     album: currentAlbum.name || "global",
                //     source: "obto",
                //     url: fullFileUrl,
                //     variants: [],
                //     domain,
                //     created_by: userName,
                //   };

                //   await http.post("/o/image/new", newimage);
                //   message.success(`Image ${fileName} saved successfully`);
                // };

                // saveImage();
            },
            onAfterResponse: (req, res) => {
                console.log({ res });
            },
        });

        // Check if there are any previous uploads to continue.
        upload.findPreviousUploads().then(function (previousUploads) {
            // Found previous uploads so we select the first one.
            if (previousUploads.length) {
                upload.resumeFromPreviousUpload(previousUploads[0]);
            }

            // Start the upload
            upload.start();
        });
    };

    // Chat History Management
    const addToChatHistory = (promptMessage) => {
        // console.log({promptMessage})
        let chatHistory = localStorage.getItem("chatHistory");
        chatHistory = chatHistory ? JSON.parse(chatHistory) : {};
        chatHistory[currentSessionId] = {
            timestamp: Date.now(),
            text: promptMessage.text,
            messages,
        };
        localStorage.setItem("chatHistory", JSON.stringify(chatHistory));
    };

    // Effects
    useEffect(() => {
        setCurrentSessionId(uid());
        let chatHistory = localStorage.getItem("chatHistory");
        if (chatHistory) {
            chatHistory = JSON.parse(chatHistory);
            let localStorageHistory = Object.keys(chatHistory).map(
                (chatSessionid) => ({
                    sessionId: chatSessionid,
                    name: chatHistory[chatSessionid].text,
                    timestamp: chatHistory[chatSessionid].timestamp || Date.now(),
                }),
            );
            setHistory(localStorageHistory);
        } else {
            setHistory([]);
        }
    }, []);

    useEffect(() => {
        if (messages.length) {
            addToChatHistory(messages[0]);
        }
    }, [messages]);

    useEffect(() => {
        // Auto-scroll to bottom when new messages arrive
        const chatElement = document.getElementById("chat");
        if (chatElement) {
            chatElement.scrollTop = chatElement.scrollHeight;
        }
    }, [messages]);

    // Event Handlers
    const handleTextChange = (e) => setMessageText(e.target.value);

    const handlePaste = (e) => {
        const pastedText = e.clipboardData.getData("text");
        const lineCount = pastedText.split("\n").length;

        if (lineCount > 15) {
            // Prevent the default paste into textarea
            e.preventDefault();
            // Store large pasted text separately
            setOriginalPastedText(pastedText);
            setPastedFileName(`Pasted Text (${lineCount} lines)`);
        }
        // If less than 15 lines, let default paste behavior occur
    };

    const handleFileChange = (info) => console.log({ fileList });

    const handleCancel = () => setOpen(false);
    const handleNewChat = () => {
        setCurrentSessionId(uid());
        setNewChat(true);
        setMessages([]);
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleSend();
        }
    };

    const handleDropDownClick = (event) => {
        setPersona(event.key);
    };

    const setFullScreen = (value) => {
        setLayoutClasses(value ? "chat-container fullscreen" : "chat-container");
    };

    const onChange = (list) => {
        setCheckedList(list);
    };

    // Message Processing
    const identifyContentType = (text) => {
        if (text.includes("<pre") && text.includes("</pre>")) {
            return "code";
        }

        if (text.includes("<think") || text.includes("</think>")) {
            return "think";
        }

        if (/<\/?[a-z][\s\S]*>/i.test(text)) {
            return "html";
        }
        return "text";
    };

    const setPromptHistory = (sessionId) => {
        console.log(sessionId);
        setCurrentSessionId(sessionId);
        let chatHistory = localStorage.getItem("chatHistory");
        if (chatHistory) {
            chatHistory = JSON.parse(chatHistory);
            console.log({ sessionId, chatHistory });
            setMessages(chatHistory[sessionId].messages);
        }
    };

    // SSE Handling
    const startSSE = async (value) => {
        let currentText = "";

        const callbacks = {
            onMessage: async (msg, ctrl) => {
                try {
                    console.log("ONMESSAGE RAW MSG:", JSON.stringify(msg));
                    const { content } = msg;

                    if (!content && typeof content === "undefined") return;

                    setShowSpinner(false);

                    currentText += content;

                    var displayText = currentText;
                    var thinkingText = "";

                    // First, extract thinking blocks
                    const thinkingRegex = /<think[^>]*>([\s\S]*?)<\/think>/gi;
                    let match;
                    while ((match = thinkingRegex.exec(currentText)) !== null) {
                        thinkingText += match[1] + "\n";
                    }

                    // Extract open thinking block (streaming)
                    const openThinkingRegex = /<think[^>]*>([\s\S]*)$/i;
                    const openMatch = openThinkingRegex.exec(currentText);
                    if (openMatch && !currentText.includes('</think>')) {
                        thinkingText += openMatch[1];
                        setIsThinking(true);
                    } else {
                        setIsThinking(false);
                    }

                    // Remove all complete and incomplete thinking tags from display
                    displayText = currentText
                        .replace(/<think[^>]*>[\s\S]*?<\/think>/gi, "")
                        .replace(/<think[^>]*>[\s\S]*$/i, "")
                        .trim();

                    // Extract metaData from content if present (tool status updates)
                    if (content.includes("[Tool Call]") || content.includes("[Tool Result]")) {
                        setToolStatus(content.trim());
                    }
                    if (content.includes("toolStatus")) {
                        setToolStatus("");
                    }

                    setMessages((prevMessages) => {
                        const newMessages = [...prevMessages];
                        newMessages[prevMessages.length - 1] = {
                            ...newMessages[prevMessages.length - 1],
                            text: markdown.render(displayText, "javascript"),
                            thinkingText: thinkingText,
                            rawText: currentText,
                        };
                        return newMessages;
                    });
                } catch (err) {
                    console.error(err);
                }
            },
            onError: (err) => {
                console.log(err);
            },
        };

        const postBody = {
            message: value,
            appName: activeApp,
            sessionId: currentSessionId,
            aiOperation: selectedAiOperation,
        };

        if (activeCollection) {
            postBody["collection"] = activeCollection;
        }

        if (activeRecordId) {
            postBody["recordId"] = activeRecordId;
        }

        await apiService.getLLMStream(postBody, callbacks);
    };

    // Modify handleSend to use the pasted text if it exists
    const handleSend = () => {
        if (messageText.trim() || originalPastedText || fileList.length > 0) {
            setToolStatus("");
            setShowSpinner(true);
            // console.log({ newChat });
            if (newChat) {
                // console.log({ newChat });
                setNewChat(false);
                setHistory([
                    ...history,
                    {
                        name: originalPastedText || messageText,
                        sessionId: currentSessionId,
                    },
                ]);
            }

            const newQuestion = {
                text: messageText + originalPastedText,
                files: fileList,
                type: "question",
            };

            setMessages((prevMessages) => [
                ...prevMessages,
                newQuestion,
                { text: "", type: "answer", files: [] },
            ]);

            setMessageText("");
            setPastedFileName(null);
            setOriginalPastedText("");
            startSSE(messageText + originalPastedText);
        } else {
            message.error("Please enter a message or upload a file.");
        }
    };

    // Message Handling

    // Utility: strip think tags from both raw and HTML-escaped text
    const stripThinkTags = (html) => {
        if (!html) return { cleanHtml: "", thinkContent: "" };
        let cleanHtml = html;
        let thinkContent = "";

        // Handle both raw and HTML-escaped tags
        const regexes = [
            /(?:<|&lt;)think[^>]*?(?:>|&gt;)([\s\S]*?)(?:(?:<|&lt;)\/think(?:>|&gt;)|$)/gi,
        ];

        regexes.forEach(regex => {
            let match;
            while ((match = regex.exec(cleanHtml)) !== null) {
                if (match[1]) thinkContent += match[1] + "\n";
            }
            // Remove full blocks
            cleanHtml = cleanHtml.replace(/(?:<|&lt;)think[^>]*?(?:>|&gt;)[\s\S]*?(?:(?:<|&lt;)\/think(?:>|&gt;)|$)/gi, "");
        });

        // Strip HTML tags from thinking content for correct rendering
        thinkContent = thinkContent.replace(/<[^>]*>/g, "").trim();

        return { cleanHtml: cleanHtml.trim(), thinkContent };
    };

    // UI Rendering

    const renderMessageContent = (msg, index) => {
        const isLastMessage = index === messages.length - 1;

        if (msg.type === "question") {
            return <div>{msg.text}</div>;
        }

        // Strip think tags at render time (handles both SSE-parsed and HTML-escaped versions)
        var parsed = stripThinkTags(msg.text);
        var cleanHtml = parsed.cleanHtml;
        var thinkContent = msg.thinkingText || parsed.thinkContent;

        return (
            <div style={{ position: "relative" }}>
                {/* LIVE TOOL STATUS INDICATOR */}
                {isLastMessage && toolStatus && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            background: "rgba(255, 255, 255, 0.1)",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            marginBottom: "8px",
                            fontSize: "11px",
                            color: "#8dcff8",
                            border: "1px solid rgba(141, 207, 248, 0.3)",
                        }}
                    >
                        <Spin size="small" />
                        <span>{toolStatus}</span>
                    </div>
                )}

                {/* UNIFIED THINKING PROCESS LOGS AND INDICATOR */}
                {(thinkContent || (isLastMessage && isThinking)) && (
                    <div style={{ marginBottom: "10px" }}>
                        <div
                            onClick={() =>
                                setShowThinking({
                                    ...showThinking,
                                    [index]: !showThinking[index],
                                })
                            }
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 12px",
                                fontSize: "12px",
                                color: "#a0a0a0",
                                cursor: "pointer",
                                userSelect: "none",
                                backgroundColor: showThinking[index] ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                borderRadius: showThinking[index] ? "4px 4px 0 0" : "4px",
                                width: "fit-content",
                            }}
                        >
                            {(isLastMessage && isThinking) ? <Spin size="small" /> : (
                                showThinking[index] ? <EyeOutlined /> : <EyeInvisibleOutlined />
                            )}
                            <span style={{ fontStyle: "italic", display: "flex", alignItems: "center", gap: "2px" }}>
                                {showThinking[index] ? "Thinking" : (isLastMessage && (isThinking || (!thinkContent && showSpinner && msg.type !== "question")) ? thinkingPhrase : "Thinking")}
                                {isLastMessage && (isThinking || (!thinkContent && showSpinner && msg.type !== "question")) ? (
                                    <span className="obto-animated-ellipsis" style={{ display: "inline-block", width: "1.5rem", textAlign: "left" }}></span>
                                ) : (
                                    <span></span>
                                )}
                            </span>
                        </div>

                        {showThinking[index] && (
                            <div
                                style={{
                                    backgroundColor: "#0d0d0d",
                                    borderLeft: "2px solid #333",
                                    padding: "8px",
                                    borderRadius: "0 4px 4px 0",
                                    fontSize: "11px",
                                    fontFamily: "monospace",
                                    color: "#aaa",
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {((isLastMessage && isThinking && !thinkContent) ? "Thinking in progress..." : thinkContent)}
                            </div>
                        )}
                    </div>
                )}

                {/* FINAL RESPONSE */}
                <div className="obgenai-markdown" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
            </div>
        );
    };

    // Dropdown Configuration
    const dropdownItems = (
        <Menu onClick={handleDropDownClick}>
            <Menu.Item key="Developer">Developer</Menu.Item>
            <Menu.Item key="Tester">Tester</Menu.Item>
            <Menu.Item key="Admin">Admin</Menu.Item>
            <Menu.Item key="User">User</Menu.Item>
        </Menu>
    );

    const deleteChat = (event, sessionId) => {
        // setCurrentSessionId(sessionId)
        event.stopPropagation();
        // console.log({sessionId});
        let chatHistory = localStorage.getItem("chatHistory");
        if (chatHistory) {
            chatHistory = JSON.parse(chatHistory);
            delete chatHistory[sessionId];
            // setMessages(chatHistory[sessionId].messages);
        }
        let localStorageHistory = Object.keys(chatHistory).map((chatSessionid) => ({
            sessionId: chatSessionid,
            name: chatHistory[chatSessionid].text,
            timestamp: chatHistory[chatSessionid].timestamp || Date.now(),
        }));
        setHistory(localStorageHistory);
        localStorage.setItem("chatHistory", JSON.stringify(chatHistory));
    };

    const showDeleteIcon = (sessionId) => {
        const tempIsHover = { ...isHover };
        tempIsHover[sessionId] = true;
        setHover(tempIsHover);
    };

    const hideDeleteIcon = (sessionId) => {
        const tempIsHover = { ...isHover };
        tempIsHover[sessionId] = false;
        setHover(tempIsHover);
    };

    const resetHistory = async () => {
        const result = await apiService.clearChatHistory(currentSessionId);
        // console.log({result});
        messageApi.open({
            type: "success",
            content: "History Reset Successfull",
            duration: 10,
        });
    };

    const renderFileMessage = (fileList) => {
        return fileList.map((file) => {
            return (
                <div style={{ margin: "10px 0" }}>
                    <Typography.Text keyboard>{file.name}</Typography.Text>
                    <Progress percent={fileProgress[file.name]} strokeColor="#B7EB8F" />
                </div>
            );
        });
    };
    // Component Rendering

    const handleDisableChat = () => {
        if (!disableChat) {
            setDisableChat(true);
        } else {
            setDisableChat(false);
        }
    };

    const handleSwitchChange = (aiOperation, checked) => {
        // if(aiOperation)
        /* const aiOperations = {
          "application_rag": {}
        } */
        let tempSelectedAiOperation = JSON.parse(JSON.stringify(aiOperation));
        tempSelectedAiOperation = { ...tempSelectedAiOperation, ...aiOperation };
        setSelectedAiOperation(tempSelectedAiOperation);
        // console.log(`switch to ${checked}`);
    };

    const toggleCollapsed = () => {
        setCollapsed(!collapsed);
    };

    // console.log("history length" + history.length);
    const items = [
        {
            key: "New Chat",
            icon: <PlusOutlined />,
            label: "New Chat",
            onClick: () => {
                handleNewChat();
            },
        },
        {
            key: `${history.length}`,
            label: "Recent Chats",
            icon: <HistoryOutlined />,
            style: {
                maxHeight: "300px",
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#404040 #1f1f1f",
            },
            children: history.map((item, index) => {
                console.log(moment(item.timestamp).fromNow());
                return {
                    key: item.sessionId,
                    label: (
                        <Button
                            type="text"
                            style={{
                                ...SIDEBAR_BUTTON_STYLE,
                                flexDirection: "column", // Add this
                                alignItems: "flex-start", // Add this
                                height: "auto", // Add this
                                padding: "8px 12px", // Add this
                            }}
                            onMouseEnter={() => {
                                showDeleteIcon(item.sessionId);
                            }}
                            onMouseLeave={() => {
                                hideDeleteIcon(item.sessionId);
                            }}
                            onClick={() => {
                                setPromptHistory(item.sessionId);
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    width: "100%",
                                }}
                            >
                                {isHover[item.sessionId] ? (
                                    <DeleteOutlined
                                        onClick={(event) => {
                                            deleteChat(event, item.sessionId);
                                        }}
                                    />
                                ) : (
                                    <MessageOutlined />
                                )}
                                <span style={{ flex: 1, textAlign: "left" }}>
                                    {item.name?.substring(0, 15)}
                                </span>
                                {item.timestamp && (
                                    <div
                                        style={{
                                            fontSize: "10px",
                                            color: "white",
                                            marginTop: "2px",
                                            marginLeft: "20px",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {moment(item.timestamp).fromNow()}
                                    </div>
                                )}
                            </div>
                        </Button>
                    ),
                };
            }),
        },
        {
            key: "Settings",
            icon: <SettingOutlined />,
            label: "Settings",
            onClick: () => {
                setOpen(true);
            },
        },
    ];

    const getTokensConsumedBySessionId = async () => {
        try {
            const result =
                await apiService.getTokensConsumedBySessionId(currentSessionId);
            console.log(result);
        } catch (err) {
            console.log({ err: new Error(err).message });
        }
    };

    const handlePreview = () => {
        setShowPreview(!showPreview);
    };

    const handleOk = () => {
        setOpen(false);
    };

    const menuScrollStyle = {
        "& .ant-menu-submenu-popup .ant-menu": {
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#404040 #1f1f1f",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar": {
            width: "6px",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar-track": {
            background: "#1f1f1f",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar-thumb": {
            background: "#404040",
            borderRadius: "3px",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar-thumb:hover": {
            background: "#505050",
        },
    };

    // //renderingSource === "idetabs" ? "400px" : "100%", // Add minimum height for tabs

    //Gemini v2
    // 1. The Main Wrapper (The "Frame")
    const getContainerStyle = () => {
        return {
            display: "flex",
            flexDirection: "column",
            height: "100%", // Fill the split panel
            overflow: "hidden", // Prevent the frame itself from scrolling
            position: "relative",
            backgroundColor: "#111", // Ensure background is consistent
        };
    };

    // 2. The Middle Section (The "Scrollable Area")
    const getChatStyle = () => {
        return {
            flex: "1 1 0", // Vital: Grow to fill space, shrink if needed
            minHeight: 0, // Vital: Allow shrinking below content size
            overflowY: "auto", // Scroll ONLY this section
            overflowX: "hidden",
            padding: "0 20px", // Add padding here instead of container
            scrollBehavior: "smooth",
            scrollbarWidth: "thin",
            scrollbarColor: "#292929 #111111",
        };
    };

    // 3. The Footer (The "Input Area")
    const getFooterStyle = () => {
        return {
            flex: "0 0 auto", // Do not grow, do not shrink. Size to content.
            padding: "10px 10px", // Padding for the input bar
            backgroundColor: "#111", // Opaque background so text doesn't scroll "under" transparently
            borderTop: "1px solid #333", // Optional: Visual separation
            zIndex: 10,
        };
    };

    // 4. The Header (New Style)
    const getHeaderStyle = () => {
        return {
            flex: "0 0 auto", // Fixed height
            padding: "10px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #333", // Separator
            backgroundColor: "#111",
        };
    };

    const flexStyle = () => {
        let style = {
            backgroundColor: "#111",
            height: "100%",
        };
        if (renderingSource === "idetabs") {
            style = {
                backgroundColor: "#111",
                height: "100vh", // CHANGED: from 100% to 100vh to fit screen exactly
                overflow: "hidden", // ADDED: Prevent any outer window scrolling
                width: "100%", // ADDED: Ensure full width
            };
        }

        return style;
    };

    return (
        <Flex style={flexStyle()} className={layoutClasses}>
            {contextHolder}
            {!["idetabs"].includes(renderingSource) ? (
                <Layout
                    style={{
                        backgroundColor: "#111",
                    }}
                >
                    <Button
                        type="primary"
                        onClick={toggleCollapsed}
                        style={{
                            marginBottom: 16,
                        }}
                    >
                        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </Button>

                    <Menu
                        mode="inline"
                        theme="dark"
                        inlineCollapsed={collapsed}
                        items={items}
                        style={menuScrollStyle}
                    />
                </Layout>
            ) : (
                ""
            )}
            <Splitter
                style={{
                    height: "100%",
                    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Splitter.Panel
                    defaultSize="50%"
                    min="40%"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        overflow: "hidden",
                    }}
                >
                    <Layout
                        style={{
                            backgroundColor: "#111",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            height: "100%",
                        }}
                    >
                        <Modal open={open} onCancel={handleCancel} onOk={handleOk}>
                            <Tabs
                                id="tools_settings"
                                defaultActiveKey="1"
                                tabPosition={"left"}
                                style={{
                                    height: 220,
                                }}
                                items={[
                                    {
                                        label: `Tools`,
                                        key: "tools",
                                        children: (
                                            <Space direction="vertical">
                                                <div>
                                                    <Switch onChange={handleSwitchChange} /> Update JS
                                                    File
                                                </div>
                                                <div>
                                                    <Switch onChange={handleSwitchChange} /> Update API
                                                </div>
                                                <div>
                                                    <Switch
                                                        defaultChecked={selectedAiOperation.application_rag}
                                                        onChange={() => {
                                                            handleSwitchChange({ application_rag: true });
                                                        }}
                                                    />{" "}
                                                    Application RAG
                                                </div>
                                            </Space>
                                        ),
                                    },
                                ]}
                            />
                        </Modal>

                        <div style={getContainerStyle()}>
                            {/* --- A. STICKY HEADER --- */}
                            {["idetabs"].includes(renderingSource) ? (
                                <div style={getHeaderStyle()}>
                                    <Space>
                                        {/* Dynamic App Name */}
                                        <Typography.Text
                                            strong
                                            style={{ color: "#fff", fontSize: "16px" }}
                                        >
                                            {activeApp || "Assist"}
                                        </Typography.Text>
                                    </Space>

                                    <Space>
                                        <SettingOutlined
                                            style={{ color: "#999", cursor: "pointer", fontSize: "16px", padding: '0 8px' }}
                                            onClick={() => setOpen(true)}
                                        />
                                        {/* Controls: Fullscreen / Close */}
                                        {/* Assuming you want to close the splitter or clear chat */}
                                        <Menu
                                            mode="horizontal"
                                            theme="dark"
                                            inlineCollapsed={collapsed}
                                            items={items}
                                            style={menuScrollStyle}
                                        />
                                        <Button
                                            type="text"
                                            icon={<CloseOutlined />}
                                            style={{ color: "#999" }}
                                            onClick={handleAiAssist} // Or your close logic
                                        />
                                    </Space>
                                </div>
                            ) : (
                                ""
                            )}

                            <div id="chat" style={getChatStyle()}>
                                {!messages.length ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            color: "#9c9c9c",
                                            alignItems: "center",
                                            fontFamily: "monospace",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Ask anything to OB Assist to improve your app
                                    </div>
                                ) : (
                                    ""
                                )}
                                {messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            marginBottom: "24px",
                                            width: "100%"
                                        }}
                                    >
                                        <div
                                            style={{
                                                backgroundColor: "transparent",
                                                padding: msg.type === "question" ? "12px 16px" : "4px 0",
                                                borderRadius: msg.type === "question" ? "12px" : "0",
                                                background: msg.type === "question" ? "#2a2a2a" : "transparent",
                                                maxWidth: msg.type === "question" ? "80%" : "100%",
                                                marginLeft: msg.type === "question" ? "auto" : "0",
                                                color: "#e5e5e5",
                                                fontSize: "13px",
                                                lineHeight: "1.6",
                                            }}
                                        >
                                            {index === messages.length - 1 &&
                                                showSpinner &&
                                                msg.type !== "question" && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 0', color: '#a0a0a0' }}>
                                                        <Spin size="small" />
                                                        <span style={{ fontStyle: 'italic', display: "flex", alignItems: "center", gap: "2px" }}>
                                                            {thinkingPhrase}<span className="obto-animated-ellipsis" style={{ display: "inline-block", width: "1.5rem", textAlign: "left" }}></span>
                                                        </span>
                                                    </div>
                                                )}
                                            {!msg.files.length ? renderMessageContent(msg) : ""}
                                            {msg.files.length ? renderFileMessage(msg.files) : ""}
                                            {identifyContentType(msg.text) === "code" && (
                                                <Space>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        style={{ color: "#666" }}
                                                        onClick={copyCodeModern}
                                                    >
                                                        <CopyOutlined /> Copy
                                                    </Button>
                                                    {showSaveSpinner ? (
                                                        <Flex align="center" gap="middle">
                                                            <Spin size="small" />
                                                        </Flex>
                                                    ) : (
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            style={{ color: "#666" }}
                                                            onClick={saveCode}
                                                        >
                                                            <DownloadOutlined /> Save
                                                        </Button>
                                                    )}
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        style={{ color: "#666" }}
                                                        onClick={handlePreview}
                                                    >
                                                        <EyeOutlined /> Preview
                                                    </Button>
                                                </Space>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={getFooterStyle()}>
                                {!disableChat ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-end", // Align input/buttons to bottom
                                            backgroundColor: "#1a1a1a",
                                            padding: "10px",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <Space>
                                            <Upload
                                                fileList={fileList}
                                                onChange={handleFile}
                                                beforeUpload={() => false}
                                                multiple
                                                showUploadList={false}
                                                style={{ borderRadius: "50%" }}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    style={{ color: "#666" }}
                                                    icon={<UploadOutlined />}
                                                >
                                                    Upload
                                                </Button>
                                            </Upload>
                                            <Button
                                                type="text"
                                                size="small"
                                                style={{ color: "#666" }}
                                                onClick={resetHistory}
                                                icon={<ReloadOutlined style={{ color: "#fff", fontSize: "1em", cursor: "pointer" }} />}
                                            />
                                        </Space>

                                        <div
                                            style={{
                                                flex: 1,
                                                marginRight: "10px",
                                                marginLeft: "10px",
                                                position: "relative", // Added to contain absolute positioned element
                                            }}
                                        >
                                            {pastedFileName && (
                                                <>
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            top: "-30px",
                                                            right: "0",
                                                            backgroundColor: "#2a2a2a",
                                                            padding: "4px 8px",
                                                            borderRadius: "4px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            zIndex: 1,
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <FileTextOutlined style={{ color: "#666" }} />
                                                        <span
                                                            style={{ color: "#fff", fontSize: "12px" }}
                                                            onClick={showPastePreview}
                                                        >
                                                            {pastedFileName}
                                                        </span>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<CloseOutlined />}
                                                            style={{ marginLeft: "auto", color: "#666" }}
                                                            onClick={() => {
                                                                setPastedFileName(null);
                                                                setOriginalPastedText("");
                                                            }}
                                                        />
                                                    </div>
                                                    <Modal
                                                        title={pastedFileName}
                                                        open={isPastePreviewOpen}
                                                        onCancel={hidePastePreview}
                                                        footer={[
                                                            <Button key="close" onClick={hidePastePreview}>
                                                                Close
                                                            </Button>,
                                                        ]}
                                                        width={800}
                                                        style={{ top: 20 }}
                                                    >
                                                        <div
                                                            style={{
                                                                maxHeight: "70vh",
                                                                overflow: "auto",
                                                                backgroundColor: "#1a1a1a",
                                                                padding: "16px",
                                                                borderRadius: "4px",
                                                                color: "#fff",
                                                                fontFamily: "monospace",
                                                                whiteSpace: "pre-wrap",
                                                                wordWrap: "break-word",
                                                            }}
                                                        >
                                                            {originalPastedText}
                                                        </div>
                                                    </Modal>
                                                </>
                                            )}

                                            <TextArea
                                                className="obgenai-textarea"
                                                rows={1}
                                                value={messageText}
                                                onChange={handleTextChange}
                                                onPaste={handlePaste}
                                                placeholder="Type your message..."
                                                style={{
                                                    width: "100%",
                                                    border: "none",
                                                    backgroundColor: "#5e5e5e",
                                                    color: "#fff",
                                                }}
                                                autoSize={{
                                                    minRows: 1,
                                                    maxRows: 10,
                                                }}
                                                onKeyPress={handleKeyPress}
                                            />
                                        </div>
                                        <SpeechCapture
                                            apiUrl="/transcribe.bto" // POST endpoint that accepts `file`
                                            onResult={(t) => {
                                                console.log("Transcribed:", t);
                                                setMessageText(t);
                                            }}
                                            useWebSocket={false} // set true + wsUrl for streaming
                                            maxDurationSec={120}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<ArrowUpOutlined style={{ fontSize: "16px" }} />}
                                            style={{
                                                backgroundColor:
                                                    messageText.trim() || fileList.length > 0
                                                        ? "#ececec"
                                                        : "#3a3a3a",
                                                color:
                                                    messageText.trim() || fileList.length > 0
                                                        ? "#000"
                                                        : "#888",
                                                border: "none",
                                                borderRadius: "50%",
                                                width: "32px",
                                                height: "32px",
                                                minWidth: "32px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginLeft: "8px"
                                            }}
                                            onClick={handleSend}
                                            disabled={!messageText.trim() && fileList.length === 0}
                                        />
                                    </div>
                                ) : (
                                    <Alert
                                        message="You have reached your token limit."
                                        type="error"
                                    />
                                )}

                                {persona && !disableChat ? (
                                    <Flex justify={"space-between"}>
                                        <Text style={{ fontSize: 12, color: "#666" }}>
                                            {/* {persona} context set.*/} OB Assist is AI and can make mistakes.
                                        </Text>
                                        <Text style={{ fontSize: 12, color: "#666" }}>
                                            Model Used:
                                            {chatMetaData.model ? chatMetaData.model : "None"} | Input
                                            Token:
                                            {chatMetaData.inputTokens
                                                ? chatMetaData.inputTokens
                                                : 0}{" "}
                                            | Output Token:
                                            {chatMetaData.outputTokens
                                                ? chatMetaData.outputTokens
                                                : 0}{" "}
                                            | Total Tokens:
                                            {chatMetaData.totalTokens ? chatMetaData.totalTokens : 0}
                                        </Text>
                                    </Flex>
                                ) : (
                                    ""
                                )}
                                {!persona && !disableChat ? (
                                    <Text style={{ fontSize: 12, color: "#666" }}>
                                        Press Enter to send, Shift + Enter for new line
                                    </Text>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    </Layout>
                </Splitter.Panel>

                {showPreview ? (
                    <Splitter.Panel defaultSize="40%" min="20%">
                        <Layout
                            style={{
                                height: "100%",
                                width: "95%",
                                padding: "1%",
                                color: "#fff",
                                backgroundColor: "#111",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100px",
                                    width: "100%",
                                }}
                            >
                                Preview Coming Soon
                            </div>
                        </Layout>
                    </Splitter.Panel>
                ) : (
                    ""
                )}
            </Splitter>
        </Flex>
    );
};

export default GenAIChatInterface;
